import React, { useEffect, useRef } from 'react';
import { ArrowRight, Lock, ShieldCheck, Stethoscope, Sparkles, Check, X } from 'lucide-react';
import BookExitPopup from '../components/BookExitPopup';
import { attachBookingPixelListener } from '../lib/bookingPixel';
import ServicesDropdown from '../components/ServicesDropdown';

const BOOKING_URL = 'https://api.leadconnectorhq.com/widget/bookings/eligibility-next-steps-call';

const BridgePage = () => {
  const calendarRef = useRef(null);

  const scrollToCalendar = () => {
    if (calendarRef.current) {
      calendarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Track that the user reached the bridge (downstream attribution)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'bridge_page_view' });
    }
  }, []);

  // Detect a successful GHL calendar booking and fire the per-origin pixel event
  // (e.g., Menopause_LeadCaptured) when the user came from a tagged landing page.
  useEffect(() => attachBookingPixelListener(), []);

  const isYou = [
    'You are a woman between 30 and 60 navigating real hormonal symptoms',
    'You have already been told your labs are "fine" but you know you are not',
    'You are open to working with a clinician who specializes in women\'s hormones',
    'You are willing to invest in your health if the plan is genuinely the right fit',
  ];

  const notYou = [
    'You are looking for a one-time prescription with no follow-up care',
    'You are hoping someone will tell you nothing is wrong without testing',
    'You are not a woman, or you are outside our current service states',
    'You are not ready to consider that hormones might be part of the picture',
  ];

  const whatToExpect = [
    {
      n: '1',
      title: 'We listen',
      body: 'Tell us what you have been experiencing. No minimizing. No dismissal. We take your symptoms seriously, even the ones you have been told are nothing.',
    },
    {
      n: '2',
      title: 'We tell you the truth',
      body: 'We will share what we think might be going on, what tests would actually answer it, and whether our program can realistically help you.',
    },
    {
      n: '3',
      title: 'You decide',
      body: 'No pressure, no hard close. If we are a fit, we walk you through getting started. If we are not, we will tell you that too, and point you toward what might be.',
    },
  ];

  return (
    <div className="bridge-page">
      <BookExitPopup />
      {/* Header */}
      <header className="bridge-nav">
        <a href="/" className="bridge-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
            alt="Glow Health Logo"
            className="brand-symbol"
          />
          <span>Glow Health</span>
        </a>
        <div className="bridge-nav-actions">
          <ServicesDropdown testIdPrefix="bridge-nav-services" />
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="bridge-hero">
          <div className="bridge-container bridge-hero-inner">
            <span className="bridge-eyebrow">Before you book, read this</span>
            <h1 className="bridge-h1">
              The free 15-minute call is not a sales pitch.<br />
              <span className="bridge-h1-accent">It is a real conversation about what is actually going on.</span>
            </h1>
            <p className="bridge-hero-sub">
              We keep this call short, focused, and honest. You get to say everything you have been holding back. We get to tell you whether we can actually help. No pressure, no commitment.
            </p>
            <div className="bridge-hero-actions">
              <button
                className="bridge-btn-primary"
                onClick={scrollToCalendar}
                data-testid="bridge-hero-cta"
              >
                Pick Your Time Below
                <ArrowRight size={18} />
              </button>
              <div className="bridge-trust-strip">
                <span><Lock size={14} /> Confidential</span>
                <span><ShieldCheck size={14} /> HIPAA compliant</span>
                <span><Stethoscope size={14} /> Licensed clinicians</span>
              </div>
            </div>
          </div>
        </section>

        {/* Pre-qualifier: Is this you / not you */}
        <section className="bridge-section bridge-qualify">
          <div className="bridge-container">
            <div className="bridge-section-head">
              <span className="bridge-eyebrow">Make sure this is the right call for you</span>
              <h2 className="bridge-h2">A quick honesty check before you book</h2>
              <p className="bridge-section-sub">
                We do not believe in being the right fit for everyone, and we will not pretend to be. Read both sides below before picking a time.
              </p>
            </div>
            <div className="bridge-qualify-grid">
              <div className="bridge-qualify-col bridge-qualify-yes">
                <h3>This call is for you if…</h3>
                <ul>
                  {isYou.map((item, i) => (
                    <li key={i}>
                      <Check size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bridge-qualify-col bridge-qualify-no">
                <h3>This call is probably not for you if…</h3>
                <ul>
                  {notYou.map((item, i) => (
                    <li key={i}>
                      <X size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* What to expect */}
        <section className="bridge-section bridge-call-section">
          <div className="bridge-container bridge-narrow">
            <div className="bridge-section-head">
              <span className="bridge-eyebrow">What to expect on the call</span>
              <h2 className="bridge-h2">15 minutes, three simple things</h2>
            </div>
            <div className="bridge-call-steps">
              {whatToExpect.map((s) => (
                <div key={s.n} className="bridge-step">
                  <span className="bridge-step-num">{s.n}</span>
                  <div>
                    <strong>{s.title}</strong>
                    <p>{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="bridge-call-note">
              That is the whole call. No upsell. No "limited time offer." Just a focused conversation between you and a clinician who specializes in this.
            </p>
          </div>
        </section>

        {/* Testimonial */}
        <section className="bridge-section bridge-testimonial-section">
          <div className="bridge-container bridge-narrow">
            <blockquote>
              <p>"After being dismissed by three doctors, someone finally listened. I cried in my first appointment, in a good way."</p>
              <cite>Karen T., 49</cite>
            </blockquote>
          </div>
        </section>

        {/* Embedded calendar */}
        <section className="bridge-section bridge-calendar-section" ref={calendarRef} id="calendar">
          <div className="bridge-container">
            <div className="bridge-section-head">
              <span className="bridge-eyebrow">Pick a time that works for you</span>
              <h2 className="bridge-h2">Book your free 15-minute call</h2>
              <p className="bridge-section-sub">
                Confidential. No charge. No commitment. Available evenings and weekends.
              </p>
            </div>
            <div className="bridge-calendar-frame">
              <iframe
                src={BOOKING_URL}
                title="Book your free 15-minute call"
                className="bridge-calendar-iframe"
                frameBorder="0"
                data-testid="bridge-calendar-iframe"
              />
            </div>
            <div className="bridge-calendar-trust">
              <span><Lock size={15} /> Your information is encrypted and HIPAA compliant.</span>
              <span><Sparkles size={15} /> Free 60-day promise on every plan.</span>
              <span><Stethoscope size={15} /> Licensed clinicians. No bots.</span>
            </div>
          </div>
        </section>

        {/* What women ask before they book */}
        <section className="bridge-section bridge-mini-faq">
          <div className="bridge-container bridge-narrow">
            <div className="bridge-section-head">
              <span className="bridge-eyebrow">Common questions</span>
              <h2 className="bridge-h2">Quick answers before you click "book"</h2>
            </div>
            <div className="bridge-faq-list">
              <div className="bridge-faq-item">
                <h3>Is the call really free?</h3>
                <p>Yes. The 15-minute call is free, and there is no obligation to start care afterward. If we are not a fit, you owe us nothing.</p>
              </div>
              <div className="bridge-faq-item">
                <h3>Will you try to sell me something?</h3>
                <p>No. The call is for clinical fit, not a sales close. If our program suits you, we will walk you through how it works. If it does not, we will say so.</p>
              </div>
              <div className="bridge-faq-item">
                <h3>What if I have already had labs done?</h3>
                <p>Bring them. Recent labs (within 3 months) save time and often save you money on the next round. Your provider will review them with you.</p>
              </div>
              <div className="bridge-faq-item">
                <h3>How does the membership work after?</h3>
                <p>If we are a fit, our membership is $99 per month, all-in for visits, plan, prescriptions management, and messaging. Labs and medications are billed separately. Cancel anytime.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bridge-section bridge-cta-section">
          <div className="bridge-container bridge-narrow bridge-cta-inner">
            <h2 className="bridge-h2">Still here? Then book the call.</h2>
            <p>
              The first 15 minutes is the hardest. Once you have actually told someone what is going on, you will already feel a little less alone.
            </p>
            <button
              className="bridge-btn-primary bridge-btn-large"
              onClick={scrollToCalendar}
              data-testid="bridge-final-cta"
            >
              Pick Your Time
              <ArrowRight size={18} />
            </button>
            <p className="bridge-cta-note">Free, confidential, and on your schedule.</p>
          </div>
        </section>
      </main>

      <footer className="bridge-footer">
        <div className="bridge-container">
          <p>
            <a href="/">myglowhealth.co</a> · Telehealth hormone care for women · HIPAA compliant
            <span className="bridge-footer-sep"> · </span>
            <a href="/privacy-policy">Privacy</a>
            <span className="bridge-footer-sep"> · </span>
            <a href="/terms-of-service">Terms</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BridgePage;
